import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';
import {
  Users,
  UserPlus,
  Search,
  Phone,
  Home,
  Mail,
  MoreVertical,
  UserMinus,
  History,
  Send,
  Clock,
  Loader2,
  AlertCircle,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

function transformTenant(t) {
  const first = t.first_name || '';
  const last = t.last_name || '';
  const initials =
    first && last ? `${first[0]}${last[0]}`.toUpperCase() : '??';
  return {
    id: t.id,
    userId: t.user_id,
    name: `${first} ${last}`.trim() || t.email,
    firstName: first,
    lastName: last,
    email: t.email,
    phone: t.phone || 'Not provided',
    propertyId: t.property_id,
    propertyName: t.property_name,
    status: t.status,
    dateAdded: t.date_added ? t.date_added.split('T')[0] : null,
    avatarInitials: initials,
  };
}

function StatusBadge({ status, hasProperty }) {
  if (status === 'active' && hasProperty) {
    return (
      <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
        Assigned
      </span>
    );
  }
  if (status === 'active' && !hasProperty) {
    return (
      <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">
        Unassigned
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
        Invite Pending
      </span>
    );
  }
  if (status === 'previous') {
    return (
      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
        Previous
      </span>
    );
  }
  return null;
}

function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteSent, setInviteSent] = useState(false);

  const [assignPropertyId, setAssignPropertyId] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', email: '', phone: '', property_id: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState(false);
  const [sendInviteLoading, setSendInviteLoading] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, first_name: '', last_name: '', email: '', phone: '', property_id: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState(false);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageBody, setMessageBody] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [messageSent, setMessageSent] = useState(false);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/api/v1/tenant/list'));
      if (!res.ok) throw new Error(`Failed to load tenants (${res.status})`);
      const data = await res.json();
      setTenants((data.tenants || []).map(transformTenant));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/api/v1/property/list'));
      if (!res.ok) throw new Error(`Failed to load properties (${res.status})`);
      const data = await res.json();
      setProperties(data.properties || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchProperties();
  }, [fetchTenants, fetchProperties]);

  const filteredTenants = tenants.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.propertyName || '').toLowerCase().includes(searchQuery.toLowerCase());
    if (viewMode === 'all') return matchesSearch;
    if (viewMode === 'active') return matchesSearch && t.status === 'active' && t.propertyId;
    if (viewMode === 'unassigned') return matchesSearch && t.status === 'active' && !t.propertyId;
    if (viewMode === 'pending') return matchesSearch && t.status === 'pending';
    if (viewMode === 'previous') return matchesSearch && t.status === 'previous';
    return matchesSearch;
  });

  const activeTenantCount = tenants.filter((t) => t.status === 'active' && t.propertyId).length;
  const previousTenantCount = tenants.filter((t) => t.status === 'previous').length;
  const unassignedCount = tenants.filter((t) => t.status === 'active' && !t.propertyId).length;
  const pendingCount = tenants.filter((t) => t.status === 'pending').length;

  const occupiedPropertyIds = new Set(
    tenants.filter((t) => t.propertyId).map((t) => t.propertyId)
  );
  const availableProperties = properties.filter((p) => !occupiedPropertyIds.has(p.id));

  const editSelectableProperties = properties.filter(
    (p) => !occupiedPropertyIds.has(p.id) || String(p.id) === editForm.property_id
  );

  async function handleInvite() {
    if (!inviteEmail || !inviteFirstName || !inviteLastName) return;

    setInviteLoading(true);
    setInviteError('');

    try {
      const response = await authFetch(getApiUrl('/api/v1/tenant/invite'), {
        method: 'POST',
        body: JSON.stringify({
          first_name: inviteFirstName,
          last_name: inviteLastName,
          email: inviteEmail,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setInviteError(data.error || 'Failed to send invite.');
        setInviteLoading(false);
        return;
      }

      setInviteSent(true);
      await fetchTenants();
      setTimeout(() => {
        setShowInviteModal(false);
        resetInviteForm();
      }, 1500);
    } catch (err) {
      console.error('Invite error:', err);
      setInviteError('An error occurred. Please try again.');
      setInviteLoading(false);
    }
  }

  async function handleAssign() {
    if (!selectedTenant || !assignPropertyId) return;
    setAssignLoading(true);
    try {
      const res = await authFetch(
        getApiUrl(`/api/v1/tenant/update/${selectedTenant.id}`),
        {
          method: 'PUT',
          body: JSON.stringify({
            property_id: Number(assignPropertyId),
            active: true,
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to assign tenant');
      }
      await fetchTenants();
      setShowAssignModal(false);
      setSelectedTenant(null);
      setAssignPropertyId('');
    } catch (err) {
      console.error('Assign error:', err);
      alert(err.message);
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleRemoveFromProperty() {
    if (!selectedTenant) return;
    setRemoveLoading(true);
    try {
      const res = await authFetch(
        getApiUrl(`/api/v1/tenant/update/${selectedTenant.id}`),
        {
          method: 'PUT',
          body: JSON.stringify({ property_id: null, active: false }),
        }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove tenant');
      }
      await fetchTenants();
      setShowRemoveConfirm(false);
      setSelectedTenant(null);
    } catch (err) {
      console.error('Remove error:', err);
      alert(err.message);
    } finally {
      setRemoveLoading(false);
    }
  }

  function resetInviteForm() {
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteSent(false);
    setInviteError('');
    setInviteLoading(false);
  }

  function resetAddForm() {
    setAddForm({ first_name: '', last_name: '', email: '', phone: '', property_id: '' });
    setAddSuccess(false);
    setAddError('');
    setAddLoading(false);
  }

  function resetEditForm() {
    setEditForm({ id: null, first_name: '', last_name: '', email: '', phone: '', property_id: '' });
    setEditSuccess(false);
    setEditError('');
    setEditLoading(false);
  }

  function openEditModal(tenant) {
    setEditForm({
      id: tenant.id,
      first_name: tenant.firstName || '',
      last_name: tenant.lastName || '',
      email: tenant.email || '',
      phone: parsePhoneDigits(tenant.phone || ''),
      property_id: tenant.propertyId ? String(tenant.propertyId) : '',
    });
    setEditSuccess(false);
    setEditError('');
    setEditLoading(false);
    setShowEditModal(true);
  }

  async function handleEditTenant() {
    if (!editForm.first_name || !editForm.last_name || !editForm.email) return;

    setEditLoading(true);
    setEditError('');

    try {
      const body = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: editForm.phone ? `+1${editForm.phone}` : null,
        property_id: editForm.property_id ? Number(editForm.property_id) : null,
      };

      const response = await authFetch(getApiUrl(`/api/v1/tenant/update/${editForm.id}`), {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        setEditError(data.error || 'Failed to update tenant.');
        setEditLoading(false);
        return;
      }

      setEditSuccess(true);
      await fetchTenants();
      setTimeout(() => {
        setShowEditModal(false);
        resetEditForm();
      }, 1500);
    } catch (err) {
      console.error('Edit tenant error:', err);
      setEditError('An error occurred. Please try again.');
      setEditLoading(false);
    }
  }

  function resetMessageForm() {
    setMessageBody('');
    setMessageSent(false);
    setMessageError('');
    setMessageLoading(false);
  }

  async function handleSendMessage() {
    if (!selectedTenant || !messageBody.trim()) return;

    setMessageLoading(true);
    setMessageError('');

    try {
      const response = await authFetch(getApiUrl('/api/v1/message/send'), {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: selectedTenant.id,
          message_body: messageBody.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessageError(data.error || 'Failed to send message.');
        setMessageLoading(false);
        return;
      }

      setMessageSent(true);
      setTimeout(() => {
        setShowMessageModal(false);
        resetMessageForm();
        setSelectedTenant(null);
        navigate('/messages');
      }, 1500);
    } catch (err) {
      console.error('Send message error:', err);
      setMessageError('An error occurred. Please try again.');
      setMessageLoading(false);
    }
  }

  function formatPhoneDisplay(digits) {
    const d = digits.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }

  function parsePhoneDigits(formatted) {
    return formatted.replace(/\D/g, '').slice(0, 10);
  }

  async function handleAddTenant() {
    if (!addForm.first_name || !addForm.last_name || !addForm.email) return;

    setAddLoading(true);
    setAddError('');

    try {
      const body = {
        first_name: addForm.first_name,
        last_name: addForm.last_name,
        email: addForm.email,
      };
      if (addForm.phone) {
        body.phone = `+1${addForm.phone}`;
      }
      if (addForm.property_id) {
        body.property_id = Number(addForm.property_id);
      }

      const response = await authFetch(getApiUrl('/api/v1/tenant/create'), {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        setAddError(data.error || 'Failed to add tenant.');
        setAddLoading(false);
        return;
      }

      setAddSuccess(true);
      await fetchTenants();
      setTimeout(() => {
        setShowAddModal(false);
        resetAddForm();
      }, 1500);
    } catch (err) {
      console.error('Add tenant error:', err);
      setAddError('An error occurred. Please try again.');
      setAddLoading(false);
    }
  }

  async function handleSendInvite(tenant) {
    setSendInviteLoading(tenant.id);
    try {
      const response = await authFetch(getApiUrl('/api/v1/tenant/invite'), {
        method: 'POST',
        body: JSON.stringify({
          first_name: tenant.firstName,
          last_name: tenant.lastName,
          email: tenant.email,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invite');
      }

      await fetchTenants();
    } catch (err) {
      console.error('Send invite error:', err);
      alert(err.message);
    } finally {
      setSendInviteLoading(null);
    }
  }

  const viewModeButtons = [
    { key: 'all', label: `All (${tenants.length})` },
    { key: 'active', label: `Active (${activeTenantCount})` },
    { key: 'unassigned', label: `Unassigned (${unassignedCount})` },
    { key: 'pending', label: `Pending (${pendingCount})` },
    { key: 'previous', label: `Previous (${previousTenantCount})` },
  ];

  const emptyStateMessages = {
    all: 'No tenants found',
    active: 'No active tenants found',
    unassigned: 'No unassigned tenants found',
    pending: 'No pending invitations',
    previous: 'No previous tenants found',
  };

  if (loading) {
    return (
      <Layout pageTitle="Tenant Management" pageSubtitle="Manage your tenants, assignments, and invitations">
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#2ECC71]" />
          <span className="ml-3 text-[#7F8C8D]">Loading tenants...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Tenant Management" pageSubtitle="Manage your tenants, assignments, and invitations">
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <AlertCircle size={48} className="mx-auto text-[#E74C3C] mb-3" />
          <p className="text-[#2C3E50] text-lg mb-1">Failed to load tenants</p>
          <p className="text-sm text-[#7F8C8D] mb-4">{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); fetchTenants(); }}
            className="px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors"
            style={{ fontWeight: 500 }}
          >
            Try Again
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Tenant Management" pageSubtitle="Manage your tenants, assignments, and invitations">
      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#3498DB] hover:bg-[#2980B9] text-white rounded-lg transition-colors"
          style={{ fontWeight: 500 }}
        >
          <Users size={20} />
          Add Tenant
        </button>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors"
          style={{ fontWeight: 500 }}
        >
          <UserPlus size={20} />
          Invite Tenant
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setViewMode('active')}
          className={`bg-white rounded-lg shadow-sm p-5 text-left transition-all ${
            viewMode === 'active' ? 'ring-2 ring-emerald-500 shadow-md' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#7F8C8D]">Active Tenants</p>
              <p className="text-3xl text-[#2C3E50] mt-1">{activeTenantCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50">
              <Users size={24} className="text-emerald-600" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setViewMode('unassigned')}
          className={`bg-white rounded-lg shadow-sm p-5 text-left transition-all ${
            viewMode === 'unassigned' ? 'ring-2 ring-amber-500 shadow-md' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#7F8C8D]">Unassigned</p>
              <p className="text-3xl text-[#2C3E50] mt-1">{unassignedCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50">
              <Home size={24} className="text-amber-500" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setViewMode('pending')}
          className={`bg-white rounded-lg shadow-sm p-5 text-left transition-all ${
            viewMode === 'pending' ? 'ring-2 ring-blue-500 shadow-md' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#7F8C8D]">Invite Pending</p>
              <p className="text-3xl text-[#2C3E50] mt-1">{pendingCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <Clock size={24} className="text-blue-500" />
            </div>
          </div>
        </button>
        <button
          onClick={() => setViewMode('previous')}
          className={`bg-white rounded-lg shadow-sm p-5 text-left transition-all ${
            viewMode === 'previous' ? 'ring-2 ring-gray-400 shadow-md' : 'hover:shadow-md'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#7F8C8D]">Previous Tenants</p>
              <p className="text-3xl text-[#2C3E50] mt-1">{previousTenantCount}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-100">
              <History size={24} className="text-gray-500" />
            </div>
          </div>
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md w-full">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7F8C8D]" />
            <input
              type="text"
              placeholder="Search tenants by name, email, or property..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#E2E8F0] bg-[#F5F5F5] focus:outline-none focus:ring-2 focus:ring-[#2ECC71]/30 focus:border-[#2ECC71] transition-colors text-sm"
            />
          </div>

          <div className="flex bg-[#F5F5F5] rounded-lg p-1 flex-wrap">
            {viewModeButtons.map((btn) => (
              <button
                key={btn.key}
                onClick={() => setViewMode(btn.key)}
                className={`px-3 py-2 rounded-md text-sm transition-colors ${
                  viewMode === btn.key
                    ? 'bg-white text-[#2C3E50] shadow-sm'
                    : 'text-[#7F8C8D] hover:text-[#2C3E50]'
                }`}
                style={{ fontWeight: viewMode === btn.key ? 500 : 400 }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tenant List */}
      {filteredTenants.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Users size={48} className="mx-auto text-[#7F8C8D] mb-3" />
          <p className="text-[#2C3E50] text-lg mb-1">{emptyStateMessages[viewMode]}</p>
          <p className="text-sm text-[#7F8C8D]">
            {searchQuery
              ? 'Try adjusting your search terms'
              : viewMode === 'pending'
              ? 'Invite a tenant and they will appear here until they sign up'
              : viewMode === 'previous'
              ? 'Previous tenants will appear here after they move out'
              : 'Invite your first tenant to get started'}
          </p>
          {(viewMode === 'all' || viewMode === 'active' || viewMode === 'unassigned') && !searchQuery && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="mt-4 px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors inline-flex items-center gap-2"
              style={{ fontWeight: 500 }}
            >
              <UserPlus size={18} />
              Invite Tenant
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTenants.map((tenant) => (
            <div
              key={tenant.id}
              onClick={() => openEditModal(tenant)}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0 text-sm ${
                    tenant.status === 'active'
                      ? 'bg-[#2ECC71]'
                      : tenant.status === 'pending'
                      ? 'bg-[#3498DB]'
                      : 'bg-gray-400'
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  {tenant.avatarInitials}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-[#2C3E50]" style={{ fontWeight: 500 }}>{tenant.name}</h4>
                    <StatusBadge status={tenant.status} hasProperty={!!tenant.propertyId} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-[#7F8C8D]">
                    <span className="flex items-center gap-1.5">
                      <Mail size={14} />
                      {tenant.email}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Phone size={14} />
                      {tenant.phone}
                    </span>
                    {tenant.propertyName && (
                      <span className="flex items-center gap-1.5">
                        <Home size={14} />
                        {tenant.propertyName}
                      </span>
                    )}
                  </div>
                  {tenant.status === 'previous' && tenant.dateAdded && (
                    <p className="text-xs text-[#7F8C8D] mt-1">
                      Added: {new Date(tenant.dateAdded).toLocaleDateString()}
                    </p>
                  )}
                  {tenant.status === 'pending' && tenant.dateAdded && (
                    <p className="text-xs text-[#7F8C8D] mt-1">
                      Invited: {new Date(tenant.dateAdded).toLocaleDateString()} — Awaiting sign-up
                    </p>
                  )}
                </div>

                {(tenant.status === 'active' || tenant.status === 'previous') && (
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <MoreVertical size={20} className="text-[#7F8C8D]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          onClick={() => openEditModal(tenant)}
                          className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                        >
                          <Pencil size={16} className="text-[#3498DB]" />
                          Edit Tenant
                        </DropdownMenuItem>
                        {tenant.phone && tenant.phone !== 'Not provided' && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowMessageModal(true);
                            }}
                            className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                          >
                            <MessageSquare size={16} className="text-[#2ECC71]" />
                            Send Message
                          </DropdownMenuItem>
                        )}
                        {tenant.status === 'active' && !tenant.userId && (
                          <DropdownMenuItem
                            onClick={() => handleSendInvite(tenant)}
                            disabled={sendInviteLoading === tenant.id}
                            className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                          >
                            {sendInviteLoading === tenant.id ? (
                              <Loader2 size={16} className="text-[#3498DB] animate-spin" />
                            ) : (
                              <Send size={16} className="text-[#3498DB]" />
                            )}
                            {sendInviteLoading === tenant.id ? 'Sending...' : 'Send Invite'}
                          </DropdownMenuItem>
                        )}
                        {tenant.status === 'active' && !tenant.propertyId && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowAssignModal(true);
                            }}
                            className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                          >
                            <Home size={16} className="text-[#2ECC71]" />
                            Assign to Property
                          </DropdownMenuItem>
                        )}
                        {tenant.status === 'previous' && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowAssignModal(true);
                            }}
                            className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                          >
                            <Home size={16} className="text-[#2ECC71]" />
                            Re-assign to Property
                          </DropdownMenuItem>
                        )}
                        {tenant.status === 'active' && tenant.propertyId && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setShowRemoveConfirm(true);
                            }}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-[#E74C3C] cursor-pointer focus:text-[#E74C3C]"
                          >
                            <UserMinus size={16} />
                            Remove from Property
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite Modal */}
      <Dialog
        open={showInviteModal}
        onOpenChange={(open) => {
          setShowInviteModal(open);
          if (!open) resetInviteForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2C3E50]">
              <UserPlus size={22} className="text-[#2ECC71]" />
              Invite New Tenant
            </DialogTitle>
            <DialogDescription>
              Send a platform invitation to your tenant. They'll receive an email to set up their account.
            </DialogDescription>
          </DialogHeader>

          {inviteSent ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send size={28} className="text-[#2ECC71]" />
              </div>
              <h4 className="text-[#2C3E50] text-lg mb-1" style={{ fontWeight: 500 }}>Invitation Sent!</h4>
              <p className="text-sm text-[#7F8C8D]">
                An invite has been sent to {inviteEmail}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    First Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Jane"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    Last Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Doe"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Email Address <span className="text-[#E74C3C]">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="tenant@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          {!inviteSent && (
            <>
              {inviteError && (
                <p className="text-sm text-[#E74C3C] text-center">{inviteError}</p>
              )}
              <DialogFooter className="gap-3 sm:gap-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    resetInviteForm();
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
                  style={{ fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  disabled={!inviteEmail || !inviteFirstName || !inviteLastName || inviteLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#2ECC71] hover:bg-[#27AE60] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  style={{ fontWeight: 500 }}
                >
                  <Send size={18} />
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Tenant Modal */}
      <Dialog
        open={showAddModal}
        onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) resetAddForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2C3E50]">
              <Users size={22} className="text-[#3498DB]" />
              Add Tenant
            </DialogTitle>
            <DialogDescription>
              Manually add a tenant to your organization. You can invite them to create an account later.
            </DialogDescription>
          </DialogHeader>

          {addSuccess ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={28} className="text-[#2ECC71]" />
              </div>
              <h4 className="text-[#2C3E50] text-lg mb-1" style={{ fontWeight: 500 }}>Tenant Added!</h4>
              <p className="text-sm text-[#7F8C8D]">
                {addForm.first_name} {addForm.last_name} has been added to your organization.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    First Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Jane"
                    value={addForm.first_name}
                    onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    Last Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Doe"
                    value={addForm.last_name}
                    onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Email Address <span className="text-[#E74C3C]">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="tenant@email.com"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Phone Number
                </label>
                <div className="flex items-center gap-2">
                  <div className="w-[40px]">
                    <Input
                      value="+1"
                      readOnly
                      className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
                    />
                  </div>
                  <Input
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={formatPhoneDisplay(addForm.phone)}
                    onChange={(e) => setAddForm({ ...addForm, phone: parsePhoneDigits(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Property
                </label>
                {availableProperties.length === 0 ? (
                  <p className="text-sm text-[#7F8C8D]">No available properties. You can assign one later.</p>
                ) : (
                  <Select
                    value={addForm.property_id}
                    onValueChange={(val) => setAddForm({ ...addForm, property_id: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a property (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProperties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          {!addSuccess && (
            <>
              {addError && (
                <p className="text-sm text-[#E74C3C] text-center">{addError}</p>
              )}
              <DialogFooter className="gap-3 sm:gap-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetAddForm();
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
                  style={{ fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTenant}
                  disabled={!addForm.first_name || !addForm.last_name || !addForm.email || addLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#3498DB] hover:bg-[#2980B9] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  style={{ fontWeight: 500 }}
                >
                  <Users size={18} />
                  {addLoading ? 'Adding...' : 'Add Tenant'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Modal */}
      <Dialog
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) resetEditForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2C3E50]">
              <Users size={22} className="text-[#3498DB]" />
              Edit Tenant
            </DialogTitle>
            <DialogDescription>
              Update this tenant's details, contact information, and property assignment.
            </DialogDescription>
          </DialogHeader>

          {editSuccess ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={28} className="text-[#2ECC71]" />
              </div>
              <h4 className="text-[#2C3E50] text-lg mb-1" style={{ fontWeight: 500 }}>Changes Saved!</h4>
              <p className="text-sm text-[#7F8C8D]">
                {editForm.first_name} {editForm.last_name} has been updated.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    First Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Jane"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                    Last Name <span className="text-[#E74C3C]">*</span>
                  </label>
                  <Input
                    placeholder="Doe"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Email Address <span className="text-[#E74C3C]">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="tenant@email.com"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Phone Number
                </label>
                <div className="flex items-center gap-2">
                  <div className="w-[40px]">
                    <Input
                      value="+1"
                      readOnly
                      className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
                    />
                  </div>
                  <Input
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={formatPhoneDisplay(editForm.phone)}
                    onChange={(e) => setEditForm({ ...editForm, phone: parsePhoneDigits(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Property
                </label>
                <Select
                  value={editForm.property_id || 'none'}
                  onValueChange={(val) =>
                    setEditForm({ ...editForm, property_id: val === 'none' ? '' : val })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a property (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No property (unassign)</SelectItem>
                    {editSelectableProperties.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {!editSuccess && (
            <>
              {editError && (
                <p className="text-sm text-[#E74C3C] text-center">{editError}</p>
              )}
              <DialogFooter className="gap-3 sm:gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    resetEditForm();
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
                  style={{ fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditTenant}
                  disabled={!editForm.first_name || !editForm.last_name || !editForm.email || editLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#3498DB] hover:bg-[#2980B9] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  style={{ fontWeight: 500 }}
                >
                  <Users size={18} />
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign to Property Modal */}
      <Dialog
        open={showAssignModal}
        onOpenChange={(open) => {
          setShowAssignModal(open);
          if (!open) {
            setSelectedTenant(null);
            setAssignPropertyId('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2C3E50]">
              <Home size={22} className="text-[#3498DB]" />
              Assign to Property
            </DialogTitle>
          </DialogHeader>

          {selectedTenant && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F5F5F5] rounded-lg">
                <div className="w-10 h-10 bg-[#2ECC71] rounded-full flex items-center justify-center text-white text-sm">
                  {selectedTenant.avatarInitials}
                </div>
                <div>
                  <p className="text-sm text-[#2C3E50]" style={{ fontWeight: 500 }}>{selectedTenant.name}</p>
                  <p className="text-xs text-[#7F8C8D]">{selectedTenant.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Select Property <span className="text-[#E74C3C]">*</span>
                </label>
                {availableProperties.length === 0 ? (
                  <div className="p-4 bg-amber-50 rounded-lg text-sm text-amber-800">
                    All properties are currently occupied. Remove a tenant from a property first.
                  </div>
                ) : (
                  <Select value={assignPropertyId} onValueChange={setAssignPropertyId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a property..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProperties.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-3 sm:gap-3">
            <button
              onClick={() => {
                setShowAssignModal(false);
                setSelectedTenant(null);
                setAssignPropertyId('');
              }}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!assignPropertyId || assignLoading}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#3498DB] hover:bg-[#2980B9] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              style={{ fontWeight: 500 }}
            >
              {assignLoading ? 'Assigning...' : 'Assign Tenant'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Modal */}
      <Dialog
        open={showRemoveConfirm}
        onOpenChange={(open) => {
          setShowRemoveConfirm(open);
          if (!open) setSelectedTenant(null);
        }}
      >
        <DialogContent className="max-w-sm">
          {selectedTenant && (
            <div className="pt-2">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserMinus size={28} className="text-[#E74C3C]" />
              </div>
              <h3 className="text-lg text-[#2C3E50] text-center mb-2" style={{ fontWeight: 600 }}>
                Remove Tenant?
              </h3>
              <p className="text-sm text-[#7F8C8D] text-center">
                Are you sure you want to remove <strong>{selectedTenant.name}</strong> from{' '}
                <strong>{selectedTenant.propertyName}</strong>? They will be moved to your previous
                tenants list.
              </p>
            </div>
          )}

          <DialogFooter className="gap-3 sm:gap-3 mt-2">
            <button
              onClick={() => {
                setShowRemoveConfirm(false);
                setSelectedTenant(null);
              }}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleRemoveFromProperty}
              disabled={removeLoading}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#E74C3C] hover:bg-[#C0392B] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              style={{ fontWeight: 500 }}
            >
              {removeLoading ? 'Removing...' : 'Remove Tenant'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog
        open={showMessageModal}
        onOpenChange={(open) => {
          setShowMessageModal(open);
          if (!open) {
            resetMessageForm();
            setSelectedTenant(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2C3E50]">
              <MessageSquare size={22} className="text-[#2ECC71]" />
              Send Message
            </DialogTitle>
            <DialogDescription>
              Start a new message thread with this tenant via SMS.
            </DialogDescription>
          </DialogHeader>

          {messageSent ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send size={28} className="text-[#2ECC71]" />
              </div>
              <h4 className="text-[#2C3E50] text-lg mb-1" style={{ fontWeight: 500 }}>Message Sent!</h4>
              <p className="text-sm text-[#7F8C8D]">
                Redirecting to messages...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedTenant && (
                <div className="flex items-center gap-3 p-3 bg-[#F5F5F5] rounded-lg">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm ${
                      selectedTenant.status === 'active'
                        ? 'bg-[#2ECC71]'
                        : selectedTenant.status === 'pending'
                        ? 'bg-[#3498DB]'
                        : 'bg-gray-400'
                    }`}
                  >
                    {selectedTenant.avatarInitials}
                  </div>
                  <div>
                    <p className="text-sm text-[#2C3E50]" style={{ fontWeight: 500 }}>{selectedTenant.name}</p>
                    <p className="text-xs text-[#7F8C8D] flex items-center gap-1">
                      <Phone size={12} />
                      {selectedTenant.phone}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
                  Message <span className="text-[#E74C3C]">*</span>
                </label>
                <textarea
                  placeholder="Type your message..."
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                />
              </div>
            </div>
          )}

          {!messageSent && (
            <>
              {messageError && (
                <p className="text-sm text-[#E74C3C] text-center">{messageError}</p>
              )}
              <DialogFooter className="gap-3 sm:gap-3">
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    resetMessageForm();
                    setSelectedTenant(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm"
                  style={{ fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={!messageBody.trim() || messageLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#2ECC71] hover:bg-[#27AE60] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                  style={{ fontWeight: 500 }}
                >
                  {messageLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  {messageLoading ? 'Sending...' : 'Send Message'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Tenants;
